import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Card,
  Icon,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  TextField,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";
import axios from "axios.js";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

// Layout components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

function QAReviewsAnalyticsPage() {
  const pageTitle = "Voice - Call Center QA Insights";
  usePageTitle(pageTitle);
  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [pageLoaded, setPageLoaded] = useState(false);

  // Macro-Level States
  const [analytics, setAnalytics] = useState(null);
  const [timeframe, setTimeframe] = useState("30");

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/voice/call_centers/qa/${ref}/insights.json?days=${timeframe}`)
        .then((result) => {
          setShowLoading(false);
          console.log(result.data.data);
          if (result.data.status === "success") {
            setAnalytics(result.data.data.data);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [timeframe]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {pageLoaded && (
        <MDBox mt={1} mb={5}>
          <MDBox mb={2} display="flex" justifyContent="space-between">
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => navigate(`/voice/call-centers/quality-control/${ref}`)}
            >
              Back to Logs
            </Button>
          </MDBox>

          {/* GLOBAL FILTERS */}
          <Card sx={{ mb: 3 }}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={3}
              flexWrap="wrap"
              gap={2}
            >
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">
                  QA Insights
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Macro-level compliance distributions across templates and key functional rubrics.
                </MDTypography>
              </MDBox>
              <TextField
                select
                label="Time Window"
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                sx={{ minWidth: "180px" }}
                SelectProps={{ style: { padding: "8px 0px" } }}
              >
                <MenuItem value="7">Past 7 Days</MenuItem>
                <MenuItem value="30">Past 30 Days</MenuItem>
                <MenuItem value="90">Past Quarter</MenuItem>
              </TextField>
            </MDBox>
          </Card>

          {analytics && (
            <MDBox>
              {/* OPERATIONAL BOUNDARY METRIC CARDS */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, textAlign: "center", borderTop: "4px solid #4caf50" }}>
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      Global Average Score
                    </MDTypography>
                    <MDTypography variant="h3" fontWeight="bold" color="success">
                      {analytics.global.avg_score}%
                    </MDTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, textAlign: "center", borderTop: "4px solid #00bbd4" }}>
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      Highest Score Recorded
                    </MDTypography>
                    <MDTypography variant="h3" fontWeight="bold" color="info">
                      {analytics.global.highest_score}%
                    </MDTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ p: 2, textAlign: "center", borderTop: "4px solid #f44336" }}>
                    <MDTypography variant="button" color="text" fontWeight="medium">
                      Lowest Score Recorded
                    </MDTypography>
                    <MDTypography variant="h3" fontWeight="bold" color="error">
                      {analytics.global.lowest_score}%
                    </MDTypography>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={3} mb={3}>
                {/* TEMPLATE BREAKDOWN MATRIX */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, height: "100%" }}>
                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                      Performance by Scorecard Template
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mb={2}>
                      How different score systems compare against each other.
                    </MDTypography>
                    {analytics.templates_breakdown.map((tmpl) => (
                      <MDBox
                        key={tmpl.title}
                        mb={3}
                        p={1.5}
                        sx={{ bgcolor: "#f8f9fa", borderRadius: "8px" }}
                      >
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <MDTypography variant="body2" fontWeight="bold">
                            {tmpl.title}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Audits: <b>{tmpl.total_audits}</b>
                          </MDTypography>
                        </MDBox>
                        <Grid container spacing={2} textAlign="center">
                          <Grid item xs={4}>
                            <MDTypography variant="xxs" color="text" display="block">
                              LOWEST
                            </MDTypography>
                            <MDTypography variant="button" fontWeight="medium" color="error">
                              {tmpl.low}%
                            </MDTypography>
                          </Grid>
                          <Grid
                            item
                            xs={4}
                            sx={{ borderLeft: "1px solid #ddd", borderRight: "1px solid #ddd" }}
                          >
                            <MDTypography variant="xxs" color="text" display="block">
                              AVERAGE
                            </MDTypography>
                            <MDTypography variant="button" fontWeight="bold" color="dark">
                              {tmpl.avg}%
                            </MDTypography>
                          </Grid>
                          <Grid item xs={4}>
                            <MDTypography variant="xxs" color="text" display="block">
                              HIGHEST
                            </MDTypography>
                            <MDTypography variant="button" fontWeight="medium" color="success">
                              {tmpl.high}%
                            </MDTypography>
                          </Grid>
                        </Grid>
                      </MDBox>
                    ))}
                  </Card>
                </Grid>

                {/* MACRO COMPLIANCE CRITERIA HEATMAP */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, height: "100%" }}>
                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                      Global Rubric Failure Map
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mb={3}>
                      Macro view of core metrics across all operations to pinpoint training
                      blindspots.
                    </MDTypography>
                    {analytics.metrics_breakdown.map((metric) => (
                      <MDBox key={metric.title} mb={2.5}>
                        <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                          <MDTypography variant="body2" fontWeight="medium">
                            {metric.title}
                          </MDTypography>
                          <MDTypography
                            variant="body2"
                            fontWeight="bold"
                            color={metric.avg >= 70 ? "success" : "error"}
                          >
                            {metric.avg}% Avg
                          </MDTypography>
                        </MDBox>
                        {/* Pure CSS Stacked Distribution Component */}
                        <MDBox
                          sx={{
                            width: "100%",
                            bgcolor: "#efefef",
                            borderRadius: "4px",
                            height: "12px",
                            overflow: "hidden",
                            display: "flex",
                          }}
                        >
                          <Tooltip title={`Floor: ${metric.low}%`}>
                            <MDBox
                              sx={{
                                width: `${metric.low}%`,
                                bgcolor: "error.main",
                                height: "100%",
                                opacity: 0.4,
                              }}
                            />
                          </Tooltip>
                          <Tooltip title={`Mean: ${metric.avg}%`}>
                            <MDBox
                              sx={{
                                width: `${metric.avg - metric.low}%`,
                                bgcolor: "warning.main",
                                height: "100%",
                              }}
                            />
                          </Tooltip>
                          <Tooltip title={`Ceiling: ${metric.high}%`}>
                            <MDBox
                              sx={{
                                width: `${metric.high - metric.avg}%`,
                                bgcolor: "success.main",
                                height: "100%",
                              }}
                            />
                          </Tooltip>
                        </MDBox>
                        <MDBox display="flex" justifyContent="space-between" mt={0.5}>
                          <MDTypography variant="xxs" color="text" fontSize={14}>
                            Min: {metric.low}%
                          </MDTypography>
                          <MDTypography variant="xxs" color="text" fontSize={14}>
                            Max: {metric.high}%
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    ))}
                  </Card>
                </Grid>
              </Grid>

              {/* TIMELINE EVALUATION TREND HISTOGRAMS */}
              <Card sx={{ p: 3 }}>
                <MDTypography variant="h6" fontWeight="medium" mb={1}>
                  Historical Consistency Trends
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={3}>
                  Chronological scoring distribution over the selected time horizon.
                </MDTypography>
                <MDBox
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="space-between"
                  height="180px"
                  pt={2}
                  px={2}
                  sx={{ borderLeft: "2px solid #ddd", borderBottom: "2px solid #ddd" }}
                >
                  {analytics.historical_trends.map((dataPoint) => (
                    <MDBox
                      key={dataPoint.date}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      style={{ flexGrow: 1 }}
                    >
                      <Tooltip
                        title={`Date: ${dataPoint.date} | Avg Score: ${dataPoint.avg_score}%`}
                      >
                        <MDBox
                          sx={{
                            width: "70%",
                            maxWidth: "75px",
                            height: `${dataPoint.avg_score}%`,
                            bgcolor: dataPoint.avg_score >= 70 ? "info.main" : "warning.main",
                            borderRadius: "4px 4px 4px 4px",
                            transition: "height 0.5s ease-on-out",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "15px",
                          }}
                        >
                          <MDTypography variant="xxs" color="white" fontWeight="bold" fontSize={14}>
                            {dataPoint.avg_score}%
                          </MDTypography>
                        </MDBox>
                      </Tooltip>
                      <MDTypography
                        variant="xxs"
                        color="text"
                        mt={1}
                        sx={{ transform: "rotate(-30deg)", whiteSpace: "nowrap" }}
                        fontSize={14}
                      >
                        {dataPoint.date.split("-").slice(1).join("/")}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
                <MDBox mt={3} display="flex" justifyContent="center" gap={3}>
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <Icon color="info" fontSize="small">
                      square
                    </Icon>
                    <MDTypography variant="caption">Optimal Zone (≥70%)</MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <Icon color="warning" fontSize="small">
                      square
                    </Icon>
                    <MDTypography variant="caption">Action Required (&lt;70%)</MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          )}
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default QAReviewsAnalyticsPage;
